export type JsonSchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array' | 'null';

export type JsonSchemaNode = {
  type?: JsonSchemaType | JsonSchemaType[];
  description?: string;
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  items?: JsonSchemaNode;
  enum?: unknown[];
  format?: string;
  $ref?: string;
  allOf?: JsonSchemaNode[];
  anyOf?: JsonSchemaNode[];
  oneOf?: JsonSchemaNode[];
  definitions?: Record<string, JsonSchemaNode>;
};

/** One renderable row in the inputs list. Either a leaf field or a group header for a nested object. */
export type IntegrationInputNode =
  | {
      kind: 'leaf';
      path: string[];
      depth: number;
      type: JsonSchemaType;
      description?: string;
      required: boolean;
      optional: boolean;
      enumValues?: string[];
    }
  | {
      kind: 'group';
      path: string[];
      depth: number;
      description?: string;
      required: boolean;
    };

const followRef = (ref: string, definitions: Record<string, JsonSchemaNode>): JsonSchemaNode | undefined => {
  const prefix = '#/definitions/';
  if (!ref.startsWith(prefix)) return undefined;
  return definitions[ref.slice(prefix.length)];
};

const resolveSchema = (
  node: JsonSchemaNode,
  definitions: Record<string, JsonSchemaNode>,
  depth = 0,
): { node: JsonSchemaNode; optional: boolean } => {
  if (depth > 10) return { node, optional: false };
  if (node.$ref) {
    const target = followRef(node.$ref, definitions);
    if (target) {
      const inner = resolveSchema(target, definitions, depth + 1);
      return {
        node: { ...inner.node, description: node.description ?? inner.node.description },
        optional: inner.optional,
      };
    }
  }
  if (node.allOf && node.allOf.length === 1) {
    const inner = resolveSchema(node.allOf[0], definitions, depth + 1);
    return {
      node: { ...inner.node, description: node.description ?? inner.node.description },
      optional: inner.optional,
    };
  }
  if (node.anyOf) {
    const nonNull = node.anyOf.filter(v => v.type !== 'null');
    const hasNull = node.anyOf.some(v => v.type === 'null');
    if (nonNull.length === 1) {
      const inner = resolveSchema(nonNull[0], definitions, depth + 1);
      return {
        node: { ...inner.node, description: node.description ?? inner.node.description },
        optional: hasNull || inner.optional,
      };
    }
  }
  if (Array.isArray(node.type)) {
    const nonNull = node.type.filter(t => t !== 'null');
    const hasNull = node.type.includes('null');
    if (nonNull.length === 1) {
      return { node: { ...node, type: nonNull[0] }, optional: hasNull };
    }
  }
  return { node, optional: false };
};

const isObjectWithProperties = (node: JsonSchemaNode): boolean =>
  node.type === 'object' && !!node.properties && Object.keys(node.properties).length > 0;

/** schemars emits properties alphabetically, which buries required fields like `routing_number`. */
const requiredFirst = (properties: Record<string, JsonSchemaNode>, required: Set<string>) =>
  Object.entries(properties).sort(([a], [b]) => Number(required.has(b)) - Number(required.has(a)));

const walk = (
  node: JsonSchemaNode,
  {
    definitions,
    path,
    required,
    out,
  }: { definitions: Record<string, JsonSchemaNode>; path: string[]; required: boolean; out: IntegrationInputNode[] },
): void => {
  const { node: resolved, optional } = resolveSchema(node, definitions);
  const depth = path.length - 1;

  if (isObjectWithProperties(resolved)) {
    out.push({ kind: 'group', path, depth, description: resolved.description, required });
    const childRequired = new Set(resolved.required ?? []);
    for (const [childName, childSchema] of requiredFirst(resolved.properties ?? {}, childRequired)) {
      walk(childSchema, {
        definitions,
        path: [...path, childName],
        required: childRequired.has(childName),
        out,
      });
    }
    return;
  }

  const type = (Array.isArray(resolved.type) ? resolved.type[0] : resolved.type) ?? 'string';
  const enumValues = Array.isArray(resolved.enum)
    ? resolved.enum.filter((v): v is string => typeof v === 'string')
    : undefined;

  out.push({
    kind: 'leaf',
    path,
    depth,
    type: type as JsonSchemaType,
    description: resolved.description,
    required,
    optional,
    enumValues,
  });
};

/** Flatten a JSON schema into a list of renderable rows (leaves + nested-object group headers). */
export const getIntegrationInputNodes = (schema: unknown): IntegrationInputNode[] => {
  if (!schema || typeof schema !== 'object') return [];
  const root = schema as JsonSchemaNode;
  if (!root.properties) return [];
  const definitions = root.definitions ?? {};
  const required = new Set(root.required ?? []);
  const out: IntegrationInputNode[] = [];
  for (const [name, child] of requiredFirst(root.properties, required)) {
    walk(child, { definitions, path: [name], required: required.has(name), out });
  }
  return out;
};

export const getLeafFields = (nodes: IntegrationInputNode[]): Extract<IntegrationInputNode, { kind: 'leaf' }>[] =>
  nodes.filter((n): n is Extract<IntegrationInputNode, { kind: 'leaf' }> => n.kind === 'leaf');

/** Dot-joined path, used for display and for building JSONPath selectors (e.g. `application.foo`). */
export const pathKey = (path: string[]): string => path.join('.');

/**
 * React Hook Form interprets `.` in a field name as a nesting separator, so a dot-path key like
 * `application.foo` would be stored as a nested object (`{ application: { foo: ... } }`) instead of
 * a single flat entry. Any code that reads these maps as a flat `Record<string, string>` (e.g. the
 * submit handler's `Object.entries(...)`) then silently drops every nested field. To keep the maps
 * flat, we key them — and name their RHF fields — with dots encoded as `__`. This round-trips as long
 * as no schema property name contains consecutive underscores, which Rust field names don't.
 */
const FORM_KEY_SEPARATOR = '__';

/** RHF-safe flat key for a schema field path (no dots). Used as the field name and map key. */
export const formFieldKey = (path: string[]): string => path.join(FORM_KEY_SEPARATOR);

/** Encode a dot-path string (e.g. `application.foo`) into its RHF-safe map key. */
export const encodeFormKey = (dotPath: string): string => dotPath.split('.').join(FORM_KEY_SEPARATOR);

/** Decode an RHF-safe map key back into its dot-path string (e.g. for a JSONPath selector). */
export const decodeFormKey = (formKey: string): string => formKey.split(FORM_KEY_SEPARATOR).join('.');

const setDeep = (obj: Record<string, unknown>, path: string[], value: unknown): void => {
  let cursor = obj;
  for (let i = 0; i < path.length - 1; i += 1) {
    const key = path[i];
    const next = cursor[key];
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[path[path.length - 1]] = value;
};

const getDeep = (obj: unknown, path: string[]): unknown => {
  let cursor: unknown = obj;
  for (const key of path) {
    if (!cursor || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
};

const containsTemplate = (raw: string): boolean => raw.includes('{{');

/**
 * Coerce a user-entered string value into the JSON-native type expected by the field's schema.
 * Values that contain template markers (`{{...}}`) are left as strings so runtime templating can
 * resolve them — backends that need a strict non-string type will parse the resolved string.
 */
const coerceFieldValue = (field: Extract<IntegrationInputNode, { kind: 'leaf' }>, raw: string): unknown => {
  if (containsTemplate(raw)) return raw;
  switch (field.type) {
    case 'boolean': {
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return raw;
    }
    case 'integer':
    case 'number': {
      const n = Number(raw);
      return Number.isFinite(n) && raw.trim() !== '' ? n : raw;
    }
    default:
      return raw;
  }
};

/**
 * Build the request body JSON from the flat form state. Boolean/number fields are coerced to
 * native JSON types when the user entered a literal value; template references are preserved as
 * strings for runtime resolution.
 */
export const inputsToBodyJson = (
  fields: Extract<IntegrationInputNode, { kind: 'leaf' }>[],
  values: Record<string, string>,
): string => {
  const obj: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[formFieldKey(field.path)];
    if (raw === undefined || raw === '') continue;
    setDeep(obj, field.path, coerceFieldValue(field, raw));
  }
  return JSON.stringify(obj, null, 2);
};

/** Parse a stored body JSON back into the flat dot-keyed form state. */
export const bodyJsonToInputs = (
  body: string | undefined,
  fields: Extract<IntegrationInputNode, { kind: 'leaf' }>[],
): Record<string, string> => {
  if (!body) return Object.fromEntries(fields.map(f => [formFieldKey(f.path), '']));
  try {
    const parsed = JSON.parse(body);
    return Object.fromEntries(
      fields.map(field => {
        const value = getDeep(parsed, field.path);
        if (value === undefined || value === null) return [formFieldKey(field.path), ''];
        if (typeof value === 'string') return [formFieldKey(field.path), value];
        if (typeof value === 'number' || typeof value === 'boolean') return [formFieldKey(field.path), String(value)];
        return [formFieldKey(field.path), JSON.stringify(value)];
      }),
    );
  } catch {
    return Object.fromEntries(fields.map(f => [formFieldKey(f.path), '']));
  }
};
